import { redirect } from "@tanstack/react-router";

import type { RouterContext } from "@/router";

import { hasRouteOrParentPermission } from "./permissions";

type BeforeLoadContext = {
  context: RouterContext;
  location: {
    pathname: string;
  };
};

/**
 * Route guard that checks authentication and permissions before loading a route
 * Redirects to login if not authenticated
 * Redirects to unauthorized page if user doesn't have permission
 */
export const requireAuthAndPermission = ({
  context,
  location
}: BeforeLoadContext) => {
  // Check authentication first
  if (!context.auth.isAuthenticated) {
    throw redirect({
      to: "/auth/login",
      search: {
        redirect: location.pathname
      }
    });
  }

  // Check route permission
  const hasPermission = hasRouteOrParentPermission(
    context.auth.userMenus,
    location.pathname
  );

  if (!hasPermission) {
    throw redirect({
      to: "/unauthorized"
    });
  }
};

/**
 * Route guard that only checks authentication
 * Use this for routes that don't need permission checking (e.g., settings, profile)
 */
export const requireAuth = ({ context, location }: BeforeLoadContext) => {
  if (!context.auth.isAuthenticated) {
    throw redirect({
      to: "/auth/login",
      search: {
        redirect: location.pathname
      }
    });
  }
};

/**
 * Route guard that checks authentication and requires superadmin role
 * Redirects to login if not authenticated
 * Redirects to unauthorized page if user is not superadmin
 */
export const requireSuperAdmin = ({ context, location }: BeforeLoadContext) => {
  if (!context.auth.isAuthenticated) {
    throw redirect({
      to: "/auth/login",
      search: {
        redirect: location.pathname
      }
    });
  }

  const userRoles = context.auth.userData?.roles || [];
  if (!userRoles.includes("superadmin")) {
    throw redirect({
      to: "/unauthorized"
    });
  }
};
